/**
 * AutoRelationService
 *
 * Automatically creates and maintains relation properties based on block content.
 * Handles task lists, wikilinks, embedded objects, and bidirectional backlinks.
 *
 * Features:
 * - Auto-create "Tasks" relation for task list blocks
 * - Auto-create "Linked Objects" relation for wikilinks
 * - Auto-create "Backlinks" relation for reverse links
 * - Bidirectional relation management
 * - Real-time sync when blocks change
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { blockLinkParser } from './BlockLinkParser';
import { propertyRelationService } from './PropertyRelationService';
import type { Property } from '../models/types';

export class AutoRelationService {
  // Property names for auto-created relations
  private readonly TASKS_PROPERTY = 'Tasks';
  private readonly LINKED_OBJECTS_PROPERTY = 'Linked Objects';
  private readonly BACKLINKS_PROPERTY = 'Backlinks';

  /**
   * Sync all auto-relations for an object
   * Call this after blocks are saved/updated
   */
  async syncObjectRelations(objectId: string): Promise<void> {
    try {
      console.log('[AutoRelationService] Syncing relations for object:', objectId);

      // Extract all references from blocks
      const refs = await blockLinkParser.extractObjectReferences(objectId);

      // Sync task list relations
      if (refs.taskIds.length > 0) {
        await this.syncTaskListRelations(objectId, refs.taskIds);
      }

      // Sync wikilink relations (excluding tasks since they're handled separately)
      const nonTaskWikiLinks = refs.wikiLinkIds.filter(id => !refs.taskIds.includes(id));
      if (nonTaskWikiLinks.length > 0 || refs.widgetIds.length > 0) {
        const allLinkedObjects = [...nonTaskWikiLinks, ...refs.widgetIds];
        await this.syncWikiLinkRelations(objectId, allLinkedObjects);
      }

      // Sync bidirectional backlinks
      await this.syncBidirectionalLinks(objectId, refs.allLinkedIds);

      console.log('[AutoRelationService] Sync complete:', {
        objectId,
        tasks: refs.taskIds.length,
        linkedObjects: nonTaskWikiLinks.length + refs.widgetIds.length,
      });
    } catch (error) {
      console.error('[AutoRelationService] Error syncing relations:', error);
      // Don't throw - we don't want to block saves
    }
  }

  /**
   * Sync "Tasks" relation property with task list contents
   */
  private async syncTaskListRelations(objectId: string, taskIds: string[]): Promise<void> {
    // Get or create "Tasks" property
    let tasksProperty = await this.findOrCreateRelationProperty(
      objectId,
      this.TASKS_PROPERTY,
      'multi-relation'
    );

    if (!tasksProperty) {
      console.warn('[AutoRelationService] Failed to create Tasks property');
      return;
    }

    // Get current relations
    const currentRelations = await propertyRelationService.getRelatedObjects(
      tasksProperty.id,
      objectId
    );
    const currentTaskIds = new Set(currentRelations.map(obj => obj.id));

    // Add new task relations
    for (const taskId of taskIds) {
      if (!currentTaskIds.has(taskId)) {
        try {
          await propertyRelationService.createRelation(
            tasksProperty.id,
            objectId,
            taskId
          );
          console.log('[AutoRelationService] Added task relation:', taskId);
        } catch (error) {
          console.warn('[AutoRelationService] Failed to add task relation:', error);
        }
      }
    }

    // Remove task relations that are no longer in the task list
    const taskIdSet = new Set(taskIds);
    for (const relation of currentRelations) {
      if (!taskIdSet.has(relation.id)) {
        try {
          // Find and remove the relation
          const relationRecord = await db.propertyRelations
            .where({
              propertyId: tasksProperty.id,
              sourceObjectId: objectId,
              targetObjectId: relation.id,
            })
            .first();

          if (relationRecord) {
            await propertyRelationService.removeRelation(
              tasksProperty.id,
              objectId,
              relation.id
            );
            console.log('[AutoRelationService] Removed task relation:', relation.id);
          }
        } catch (error) {
          console.warn('[AutoRelationService] Failed to remove task relation:', error);
        }
      }
    }
  }

  /**
   * Sync "Linked Objects" relation property with wikilinks
   */
  private async syncWikiLinkRelations(
    objectId: string,
    linkedObjectIds: string[]
  ): Promise<void> {
    // Get or create "Linked Objects" property
    let linkedProperty = await this.findOrCreateRelationProperty(
      objectId,
      this.LINKED_OBJECTS_PROPERTY,
      'multi-relation'
    );

    if (!linkedProperty) {
      console.warn('[AutoRelationService] Failed to create Linked Objects property');
      return;
    }

    // Get current relations
    const currentRelations = await propertyRelationService.getRelatedObjects(
      linkedProperty.id,
      objectId
    );
    const currentLinkedIds = new Set(currentRelations.map(obj => obj.id));

    // Add new linked object relations
    for (const linkedId of linkedObjectIds) {
      if (!currentLinkedIds.has(linkedId)) {
        try {
          await propertyRelationService.createRelation(
            linkedProperty.id,
            objectId,
            linkedId
          );
          console.log('[AutoRelationService] Added linked object relation:', linkedId);
        } catch (error) {
          console.warn('[AutoRelationService] Failed to add linked object relation:', error);
        }
      }
    }

    // Remove relations that are no longer linked
    const linkedIdSet = new Set(linkedObjectIds);
    for (const relation of currentRelations) {
      if (!linkedIdSet.has(relation.id)) {
        try {
          const relationRecord = await db.propertyRelations
            .where({
              propertyId: linkedProperty.id,
              sourceObjectId: objectId,
              targetObjectId: relation.id,
            })
            .first();

          if (relationRecord) {
            await propertyRelationService.removeRelation(
              linkedProperty.id,
              objectId,
              relation.id
            );
            console.log('[AutoRelationService] Removed linked object relation:', relation.id);
          }
        } catch (error) {
          console.warn('[AutoRelationService] Failed to remove linked object relation:', error);
        }
      }
    }
  }

  /**
   * Sync bidirectional "Backlinks" for all linked objects
   * When A links to B, B should have a backlink to A
   */
  private async syncBidirectionalLinks(
    sourceObjectId: string,
    targetObjectIds: string[]
  ): Promise<void> {
    for (const targetId of targetObjectIds) {
      try {
        // Get or create "Backlinks" property on target object
        let backlinksProperty = await this.findOrCreateRelationProperty(
          targetId,
          this.BACKLINKS_PROPERTY,
          'multi-relation'
        );

        if (!backlinksProperty) continue;

        // Check if backlink already exists
        const existingRelation = await db.propertyRelations
          .where({
            propertyId: backlinksProperty.id,
            sourceObjectId: targetId,
            targetObjectId: sourceObjectId,
          })
          .first();

        if (!existingRelation) {
          // Create backlink (skip circular check - backlinks are bidirectional by design)
          await propertyRelationService.createRelation(
            backlinksProperty.id,
            targetId,
            sourceObjectId,
            0,
            true // skipCircularCheck
          );
          console.log('[AutoRelationService] Added backlink:', {
            from: sourceObjectId,
            to: targetId,
          });
        }
      } catch (error) {
        console.warn('[AutoRelationService] Failed to create backlink:', error);
      }
    }
  }

  /**
   * Find or create a relation property on an object
   */
  private async findOrCreateRelationProperty(
    objectId: string,
    propertyName: string,
    type: 'relation' | 'multi-relation'
  ): Promise<Property | null> {
    try {
      // Look for existing property
      const existingProperty = await db.properties
        .where({ objectId, name: propertyName })
        .first();

      if (existingProperty) {
        return existingProperty;
      }

      // Create new property
      // Note: Relation properties store data in property_relations table,
      // but Supabase properties.value column is NOT NULL, so we provide empty array
      const { dataRepository } = await import('../repository/DataRepository');
      const newProperty = await dataRepository.createProperty(
        objectId,
        propertyName,
        type,
        [] // Empty array for relation properties (satisfies NOT NULL constraint)
      );

      console.log('[AutoRelationService] Created auto-relation property:', propertyName);
      return newProperty;
    } catch (error) {
      console.error('[AutoRelationService] Failed to find/create property:', error);
      return null;
    }
  }

  /**
   * Clean up orphaned backlinks when an object is deleted or links are removed
   */
  async cleanupBacklinks(objectId: string): Promise<void> {
    try {
      // Find all objects that have this object in their Backlinks
      const backlinkProperties = await db.properties
        .where({ name: this.BACKLINKS_PROPERTY })
        .toArray();

      for (const property of backlinkProperties) {
        const relations = await db.propertyRelations
          .where({
            propertyId: property.id,
            targetObjectId: objectId,
          })
          .toArray();

        for (const relation of relations) {
          await propertyRelationService.removeRelation(
            property.id,
            relation.sourceObjectId,
            objectId
          );
          console.log('[AutoRelationService] Cleaned up backlink:', relation.id);
        }
      }
    } catch (error) {
      console.error('[AutoRelationService] Error cleaning up backlinks:', error);
    }
  }
}

// Singleton instance
export const autoRelationService = new AutoRelationService();
